/**
 * Her clock — one answer to "what day is it" and "what hour is it", for
 * everything that speaks in her voice.
 *
 * The app had two clocks. The mood used `localDateString(now, deviceZone)`,
 * while the cooled shade, the ambient slot and the pet counters used raw
 * `getFullYear/getMonth/getDate`, which is the *device's* idea of the day
 * rather than the account's. Those agree at home and disagree the moment the
 * phone and the profile are in different zones — a diary that rolled over
 * hours before her sulk did, on the same screen.
 *
 * The profile's zone wins, because it is the zone the diary is kept in and
 * therefore the one her day is measured in. The device zone is the fallback
 * for a signed-out screen, where there is no account to disagree with.
 */

import { deviceTimeZone, localDateString } from '@gramas/shared'
import { currentProfile } from '../screens/useProfile.js'

function herZoneName(): string {
  return currentProfile()?.tz || deviceTimeZone()
}

/** `YYYY-MM-DD` in her zone. */
export function herToday(instant: Date = new Date()): string {
  return localDateString(instant, herZoneName())
}

/** The wall-clock hour, 0–23, in her zone. */
export function herHour(instant: Date = new Date()): number {
  try {
    const hour = new Intl.DateTimeFormat('en-GB', {
      timeZone: herZoneName(),
      hour: '2-digit',
      hourCycle: 'h23',
    }).format(instant)
    const value = Number(hour)
    return Number.isFinite(value) ? value : instant.getHours()
  } catch {
    // An unknown zone on the profile should cost the joke, not the screen.
    return instant.getHours()
  }
}

/** 0 = Sunday, matching `Date#getDay` — the Wednesday register reads this. */
export function herWeekday(instant: Date = new Date()): number {
  const [y, m, d] = herToday(instant).split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}
